import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import { useZoom } from 'app/utils/customFunctions'
import { motion, useScroll, useTransform } from 'framer-motion'
import React, { memo, useRef } from 'react'

export interface Props {
  description?: string
  image?: ImageProps
  label?: string
  title?: string
}

export const VisualChef = memo(function VisualChef({
  description,
  image,
  label,
  title,
}: Props) {
  if (!title) {
    return null
  }

  const containerRef = useRef<any>()

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'center end'],
  })

  return (
    <Container ref={containerRef}>
      {label ? <Label tag="h2" text={label} /> : null}

      {image ? (
        <Background>
          <Inner style={{ scale: useZoom(scrollYProgress, [1.3, 1]) }}>
            <Image {...image} />
          </Inner>
        </Background>
      ) : null}

      <Wrapper>
        {title ? <Title tag="h3" text={title} /> : null}

        {description ? (
          <FadeInUp>
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </FadeInUp>
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  margin-top: 9.75rem;
  padding-bottom: 9rem;
  position: relative;
  text-align: center;
  &:before {
    content: '';
    width: 100%;
    height: calc(100% - 15.556vw - 3rem);
    background: url('/background-light.webp') no-repeat top center;
    background-size: cover;
    position: absolute;
    bottom: 0;
    left: 0;
  }

  @media (max-width: 1199px) {
    margin-top: 5.25rem;
    padding-bottom: 5.25rem;
    &:before {
      content: '';
      height: calc(100% - 11.25rem);
    }
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}

  @media (max-width: 1199px) {
    margin-bottom: 2rem;
  }
`

const Background = styled.div`
  aspect-ratio: 1;
  width: 31.111vw;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  border: 1.667vw solid ${({ theme }) => theme.colors.variants.neutralLight3};
  border-radius: 50%;
  margin: auto;
  overflow: hidden;
  position: relative;

  @media (max-width: 1199px) {
    width: 18.875rem;
    border-width: 1rem;
  }
`

const Inner = styled(motion.div)`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}
  }
`

const Wrapper = styled.div`
  margin: 2rem auto 0;

  @media (max-width: 1199px) {
    padding: 0 2rem;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.xm}
`

const Description = styled.div`
  ${styles.description}

  max-width: 42rem;
  margin: 1.5rem auto 0;

  @media (max-width: 1199px) {
    max-width: none;
  }
`
